import { AddUserIcon, EditIcon } from '~/svgs';
import { NextLink, Translate, useTranslate } from '~/i18n';
import useUserProfile from '~/apis/useUserProfile';
import { Button } from '~/components/common/button';
import React, { useEffect, useState } from 'react';
import Pagination from '../common/pagination';
import Search from '~/components/common/search';
import { Section } from '../common/section';
import { UISelect } from '~/components/common/forms';
import { SubmitLoader } from '../common/loaders/submit';
import cn from 'classnames';
import { getStatus } from '.';
import { useGetVisaRequestsList } from '~/apis/dhamen';
import { useRouter } from 'next/router';
import { useAuth } from '~/auth';

export const DhamenRequestsList = () => {
   const router = useRouter();
   const { user, updateUser } = useAuth();
   const { translate, lang } = useTranslate();
   const [pageNumber, setPageNumber] = useState<number>(1);
   const [search_term, setSearchTerm] = useState<string[] | null>(null);
   const [sortBy, setSortBy] = useState('');
   const {
      data: QueryData,
      isError,
      isLoading,
   } = useGetVisaRequestsList({
      search_term: search_term || undefined,
      sortBy: sortBy || undefined,
      pageNumber,
   });
   const { data: userProfileData } = useUserProfile({
      onSuccess: data => {
         if (data && data.status) {
            updateUser({ ...user, ...data });
         }
      },
   });

   useEffect(() => {
      setPageNumber(1);
   }, [sortBy]);

   const sortOptions = [
      { value: 'asc', label: translate({ id: 'reviews:date asc' }) },
      { value: 'desc', label: translate({ id: 'reviews:date desc' }) },
   ];

   if (isError) return <div></div>;

   return (
      <>
         <Section className="py-8 mt-5">
            <div className="px-4 space-y-5 lg:px-8">
               <div className="flex flex-col justify-between gap-4 text-black md:flex-row md:columns-2 ">
                  <div>
                     <div className="mb-3 text-xl font-bold sm:text-3xl">
                        <Translate id="visasManager:dhamen requests" />
                     </div>
                     <div className="text-sm whitespace-pre-line">
                        <Translate id="visasManager:dhamen requests description" />
                     </div>
                  </div>
                  <div className="flex items-start gap-4">
                     <Button
                        onClick={() =>
                           router.push(`/${lang}/umrah-requests/supervisor-and-arrival-details`)
                        }
                        disabled={!!(userProfileData?.status !== 'Active')}
                        className="w-full h-12 sm:w-60"
                        theme="primary">
                        <Translate id="visasManager:create a new trip" />
                     </Button>
                  </div>
               </div>

               <div className="h-full">
                  <div className="flex flex-col mb-5 space-y-2 xs:space-y-0 xs:flex-row xxs:justify-between">
                     <div className="grid w-full grid-cols-2 gap-6 lg:grid-cols-4 lg:grid-rows-1">
                        <div className="col-span-3 lg:col-span-3">
                           <Search
                              onSearch={(text: string[]) => setSearchTerm(text)}
                              isLoading={false}
                              placeholder={translate({ id: 'visasManager:search requests here' })}
                           />
                        </div>

                        <div className="col-span-3 lg:col-span-1">
                           <UISelect
                              onChange={(val: any) => setSortBy(val?.value || null)}
                              options={sortOptions}
                              groupClassName="!mb-0"
                              placeholder={<Translate id="bookings:sort by" />}
                              value={sortOptions.filter((item: any) => item.value === sortBy)}
                              isClearable={true}
                              instanceId="type-search"
                           />
                        </div>
                     </div>
                  </div>

                  <div className="h-full overflow-auto">
                     {isLoading ? (
                        <div className="flex justify-center h-36">
                           <SubmitLoader />
                        </div>
                     ) : null}
                     <div className="grid grid-cols-4">
                        <div className="col-span-4">
                           <table className="w-full text-sm min-w-[550px]">
                              <thead className="uppercase">
                                 <tr>
                                    <th className="px-4 text-left text-gray-400 bg-gray-100 rtl:text-right h-11 first:rounded-md last:rounded-md">
                                       <Translate id="visasManager:name" />
                                    </th>

                                    <th className="text-left text-gray-400 bg-gray-100 rtl:text-right h-11 first:rounded-md last:rounded-md">
                                       <Translate id="visasManager:request number" />
                                    </th>
                                    <th className="text-left text-gray-400 bg-gray-100 rtl:text-right h-11 first:rounded-md last:rounded-md">
                                       <Translate id="visasManager:date created" />
                                    </th>

                                    <th className="text-left text-gray-400 bg-gray-100 rtl:text-right h-11 first:rounded-md last:rounded-md">
                                       <Translate id="visasManager:status" />
                                    </th>

                                    <th className="px-4 text-right text-gray-400 bg-gray-100 rtl:text-left h-11 first:rounded-md last:rounded-md">
                                       <Translate id="visasManager:actions" />
                                    </th>
                                 </tr>
                                 {QueryData?.data.length === 0 && (
                                    <td colSpan={5} className="py-6 text-center">
                                       <span className="w-full py-8 text-xs text-center xl:text-sm text-grey-200">
                                          <Translate id="visasManager:you haven’t created any trips" />
                                       </span>
                                    </td>
                                 )}
                              </thead>

                              <tbody>
                                 {QueryData?.data.map((request: any) => (
                                    <tr
                                       className="border-b border-gray-200 border-dashed hover:bg-gray-50"
                                       key={request?._id}>
                                       <td className="py-4 ltr:pl-4 rtl:pr-4">
                                          <NextLink
                                             href={`/umrah-requests/${request?._id}`}
                                             className="underline">
                                             {request?.name}
                                          </NextLink>
                                       </td>
                                       <td className="py-4">{request?.applicationNo}</td>
                                       <td className="py-4">
                                          {request?.created_at?.split(' ')?.[0]}
                                       </td>
                                       <td className="py-4">
                                          <div className="flex items-center gap-1">
                                             <span
                                                className={cn(
                                                   getStatus(request?.status, 'bg'),
                                                   'w-2 h-2 rounded-full flex'
                                                )}></span>
                                             <span
                                                className={cn(getStatus(request?.status, 'text'))}>
                                                {request?.status}
                                             </span>
                                          </div>
                                       </td>
                                       <td className="py-4 ltr:pr-4 rtl:pl-4 ltr:text-right rtl:text-left">
                                          <div
                                             className={cn(
                                                'flex items-center justify-end w-full gap-4',
                                                request?.status.toLowerCase() === 'under review' &&
                                                   'opacity-20 cursor-not-allowed pointer-events-none'
                                             )}>
                                             {request?.status.toLowerCase() !== 'approved' && (
                                                <NextLink
                                                   href={`/umrah-requests/${request?._id}/add-applicant`}
                                                   className={cn(
                                                      'flex items-center justify-center gap-2 text-sm font-semibold text-grey-100 h-fit',
                                                      request?.visitors?.length >= 25 &&
                                                         'disabled pointer-events-none opacity-40 !cursor-not-allowed hover:cursor-not-allowed'
                                                   )}>
                                                   <AddUserIcon className="w-4 h-4" />
                                                   <Translate id="visasManager:add applicant" />
                                                </NextLink>
                                             )}
                                             <NextLink
                                                href={`/umrah-requests/${request?._id}/edit/supervisor-and-arrival-details`}
                                                className="flex items-center justify-center gap-2 text-sm font-semibold text-grey-100 h-fit">
                                                <EditIcon />
                                                <Translate id="common:edit" />
                                             </NextLink>

                                             {/* <Button
                                                className="flex items-center !text-sm font-semibold !p-0"
                                                onClick={() => {}}>
                                                <div className="flex items-center gap-2 text-states-warning h-fit">
                                                   <TrashIconOutline />
                                                   <Translate id="common:delete" />
                                                </div>
                                             </Button> */}
                                          </div>
                                       </td>
                                    </tr>
                                 ))}
                              </tbody>
                           </table>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </Section>
         <div className="flex justify-center my-5">
            <Pagination
               back={() => setPageNumber(prev => prev - 1)}
               next={() => setPageNumber(prev => prev + 1)}
               currentPage={QueryData?.current_page ?? ''}
               lastPage={QueryData?.last_page ?? ''}
               numberOfElementsInOnePage={QueryData?.per_page ?? ''}
               totalElements={QueryData?.total ?? ''}
            />
         </div>
      </>
   );
};
